import some from "lodash/some";
import * as React from "react";

import { MemberSetModel } from "@app/members";
import { Roles, UserContextProps, withUserContext } from "@app/user";
import CreateProject from "./createProject";
import { QESButton, QESSelect, QESTextInput } from "@app/shared/qes-standards";
import { ProjectOverrideMemberSetType } from "../enumerators";
import { JSX } from "react";

interface CreateMemberTableProps {
  createProjectModel: CreateProject;
  deleteAltMembershipRow: (index: number) => void;
  generateAlternateMemberships: () => void;
  handleAlternativeMemberPopAliasChange: (alias: string, index: number) => void;
  handleAlternativeMemberPopChange: (memberPopId: number, index: number) => void;
  handleNewAlternativeMemberPop: () => void;
  handleMemberPopAliasChange2: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleMemberPopAliasChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleMemberPopChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleMemberPopChange2: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleRiskPopChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  isZipProject: boolean;
  projectMemberPopulations: MemberSetModel[];
}

interface MemberPopHandlers {
  defaultOptionCopy: string;
  onPopChange: (e: React.ChangeEvent<HTMLSelectElement>, index?: number) => void;
  onAliasChange: (e: React.ChangeEvent<HTMLInputElement>, index?: number) => void;
}

interface MemberTableState {
  altMemberPopOptions: JSX.Element[];
  disableAdd: boolean;
}

/* eslint-disable sort-keys */
class CreateMemberTable extends React.Component<CreateMemberTableProps & UserContextProps, MemberTableState> {

  constructor(props: CreateMemberTableProps & UserContextProps) {
    super(props);
    const newOptions = this.getAltMemberPopOptions();
    this.state = {
      altMemberPopOptions: newOptions,
      disableAdd: this.getShouldDisableAdd(),
    };
  }

  public componentDidUpdate(prevProps: CreateMemberTableProps & UserContextProps) {
    if ((prevProps.createProjectModel.createProjectOverrideMemberSets !== this.props.createProjectModel.createProjectOverrideMemberSets)) {
      const newOptions = this.getAltMemberPopOptions();
      this.setState({
        altMemberPopOptions: newOptions,
        disableAdd: this.getShouldDisableAdd(),
      });
    }
  }
  private readonly allMemberPopOptions = this.props.projectMemberPopulations.map((option) => (
    option.hasActivatedVersions ?
      <option key={option.memberSetId} value={option.memberSetId}>{option.resource.name}</option> :
      <option key={option.memberSetId} value={option.memberSetId} disabled={true}>{option.resource.name} - No Activated Versions</option>
  ));

  public render() {
    return (
      <div className="form-group">
        <React.Fragment>
          <div className="row">
            <div className="col-2 label-s">Member Population Type</div>
            <div className="col-4 label-s">Member Population</div>
            <div className="col-3 label-s">Alias</div>
          </div>
          {this.props.createProjectModel.createProjectOverrideMemberSets.map((sr, index) => {
            if (this.props.isZipProject && sr.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Risk) {
              return null;
            }
            const memberPopId = this.getRowMemberPopId(sr.projectOverrideMemberSetType, index);
            const alias = this.getRowAlias(sr.projectOverrideMemberSetType, index);
            return this.renderMembershipRow(sr.projectOverrideMemberSetType, memberPopId, alias, sr.memberSetId, index);
          })}
          <QESButton qesType="secondary" onClick={this.props.handleNewAlternativeMemberPop} className="col-3 text-left" disabled={this.state.disableAdd} qesTestId="new-project__add-alternate-membership"><i className="fa fa-plus mr-2" /> Add Alternate Member Population</QESButton>
        </React.Fragment>
      </div>
    );
  }

  private getAltMemberPopsInUse = () => {
    return this.props.createProjectModel.createProjectOverrideMemberSets.filter((oms) => oms.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Alternate).map((oms) => oms.memberSetId).filter((oms) => !!oms);
  }

  private getAltMemberPopOptions = () => {
    const idsInUse = this.getAltMemberPopsInUse();
    return this.props.projectMemberPopulations.map((option) => (
      option.hasActivatedVersions ?
        idsInUse.includes(option.memberSetId) ?
          <option key={option.memberSetId} value={option.memberSetId} disabled={true}>{option.resource.name}</option> :
          <option key={option.memberSetId} value={option.memberSetId}>{option.resource.name}</option> :
        <option key={option.memberSetId} value={option.memberSetId} disabled={true}>{option.resource.name} - No Activated Versions</option>
    ));
  }

  private getShouldDisableAdd = (): boolean => {
    return this.props.createProjectModel.createProjectOverrideMemberSets.filter((oms) => oms.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Alternate).length >= this.allMemberPopOptions.length;
  }

  private renderMembershipRow = (type: string, memberPopulation: string, alias: string, memberSetId: number, index: number) => {
    const otherAliases = new Set(this.props.createProjectModel.createProjectOverrideMemberSets.filter((oms) => oms.memberSetId !== memberSetId).map((oms) => oms.alias));
    return (
      <div className="row mb-3" key={`${index}-${memberPopulation}`}>
        <div className="col-2 d-inline-flex align-items-center label-s">{type}</div>

        <div className="col-4">
          <QESSelect
            className='form-control'
            label="Member Population"
            hideLabel={true}
            testId={`new-project__member-pop-${index}`}
            value={memberPopulation}
            onChange={(e) => this.memberPopHandlers[type].onPopChange(e, index)}
          >
            <option>{this.memberPopHandlers[type].defaultOptionCopy}</option>
            {type === ProjectOverrideMemberSetType.Alternate ? this.state.altMemberPopOptions : this.allMemberPopOptions}
          </QESSelect>
        </div>

        <div className="col-3">
          {type !== ProjectOverrideMemberSetType.Risk && (
            <QESTextInput
              testId={`new-project__member-pop-alias-${index}`}
              label="Member Set Alias"
              hideLabel={true}
              onChange={(e) => this.memberPopHandlers[type].onAliasChange(e, index)}
              value={alias}
              unrequiredValidation={true}
              isValid={!alias || !otherAliases.has(alias)}
              wasFocusedExternally={true}
            />
          )}
        </div>

        <div className="col-2">
          {type === ProjectOverrideMemberSetType.Alternate && (
            <QESButton
              className="transparent-button-del pl-0"
              qesTestId={`new-project__delete-member-pop-${index}`}
              onClick={() => this.props.deleteAltMembershipRow(index)}
              qesType="secondary-danger"
              aria-label="Delete Alternate Member Population"
            >
              <i className="fa fa-trash fa-lg" />
            </QESButton>
          )}
        </div>
      </div>
    );
  }

  private getRowMemberPopId = (memberSetType: string, index: number): string => {
    let memberPopId = "";
    if (memberSetType === ProjectOverrideMemberSetType.Primary && this.props.createProjectModel.memberSetId) {
      memberPopId = this.props.createProjectModel.memberSetId.toString();
    } else if (memberSetType === ProjectOverrideMemberSetType.Secondary && this.props.createProjectModel.memberSetId2) {
      memberPopId = this.props.createProjectModel.memberSetId2.toString();
    } else if (memberSetType === ProjectOverrideMemberSetType.Risk && some(this.props.user.roles, ((s) => s === Roles.MEMBERSHIP_RISK_USER)) && this.props.createProjectModel.membershipRiskMemberSetId) {
      memberPopId = this.props.createProjectModel.membershipRiskMemberSetId.toString();
    } else if (memberSetType === ProjectOverrideMemberSetType.Alternate) {
      memberPopId = this.props.createProjectModel.createProjectOverrideMemberSets[index].memberSetId?.toString();
    }
    return memberPopId;
  }

  private getRowAlias = (membershipSetType: string, index: number): string => {
    if (membershipSetType === ProjectOverrideMemberSetType.Primary) {
      return this.props.createProjectModel.memberSetAlias;
    } else if (membershipSetType === ProjectOverrideMemberSetType.Secondary) {
      return this.props.createProjectModel.memberSetAlias2;
    } else if (membershipSetType === ProjectOverrideMemberSetType.Alternate) {
      return this.props.createProjectModel.createProjectOverrideMemberSets[index].alias || "";
    }
  }

  private updateRowAlias = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const alias = e.target.value;
    this.props.handleAlternativeMemberPopAliasChange(alias, index);
  }

  private updateRowId = (e: React.ChangeEvent<HTMLSelectElement>, prevMemberSetId: number) => {
    const memberSetId = +(e.target.value);
    this.props.handleAlternativeMemberPopChange(memberSetId, prevMemberSetId);
    this.props.generateAlternateMemberships();
  }

  private readonly memberPopHandlers: { [key: string]: MemberPopHandlers } = {
    [ProjectOverrideMemberSetType.Primary]: {
      defaultOptionCopy: "Select a Member Population",
      onPopChange: this.props.handleMemberPopChange,
      onAliasChange: this.props.handleMemberPopAliasChange,
    },
    [ProjectOverrideMemberSetType.Secondary]:
    {
      defaultOptionCopy: "Select a Second Member Population",
      onPopChange: this.props.handleMemberPopChange2,
      onAliasChange: this.props.handleMemberPopAliasChange2,
    },
    [ProjectOverrideMemberSetType.Risk]: {
      defaultOptionCopy: "Select a Risk Analysis Member Population",
      onPopChange: this.props.handleRiskPopChange,
      onAliasChange: null,
    },
    [ProjectOverrideMemberSetType.Alternate]: {
      defaultOptionCopy: "Select an Alternate Member Population",
      onPopChange: this.updateRowId,
      onAliasChange: this.updateRowAlias,
    }
  };
}

const WrappedCreateMemberTable = withUserContext(CreateMemberTable);

export default WrappedCreateMemberTable as React.ComponentClass<CreateMemberTableProps>;
